import {
  DatePickerProps,
  HighlightPeriod,
} from '../../../../common/DatePicker/types';
import { Sprint } from '../../types';

/**
 * Creates highlight periods for the DatePicker based on current, previous, and next sprints
 */
export const createSprintHighlightPeriods = (
  // TODO: Remove this function if we don't need to highlight multiple sprints and  update date picker as well
  sprints: Sprint[] | undefined,
  selectedSprint: Sprint | null,
): {
  lastPeriod?: HighlightPeriod;
  currentPeriod?: HighlightPeriod;
  nextPeriod?: HighlightPeriod;
} => {
  if (!sprints?.length || !selectedSprint) {
    return {
      lastPeriod: undefined,
      currentPeriod: undefined,
      nextPeriod: undefined,
    };
  }

  const currentSprintIndex: number | undefined = sprints?.findIndex(
    (sprint) => sprint.id === selectedSprint?.id,
  );

  // Get last sprint (previous in time, next in array since sprints are ordered newest first)
  const lastSprint =
    currentSprintIndex !== undefined &&
    currentSprintIndex + 1 < (sprints?.length ?? 0)
      ? sprints?.[currentSprintIndex + 1]
      : null;

  // Get next sprint (next in time, previous in array)
  const nextSprint =
    currentSprintIndex !== undefined && currentSprintIndex - 1 >= 0
      ? sprints?.[currentSprintIndex - 1]
      : null;

  return {
    lastPeriod: lastSprint
      ? {
          start: new Date(lastSprint?.startDate || ''),
          end: new Date(lastSprint?.endDate || ''),
        }
      : undefined,
    currentPeriod:
      selectedSprint?.startDate && selectedSprint?.endDate
        ? {
            start: new Date(selectedSprint?.startDate || ''),
            end: new Date(selectedSprint?.endDate || ''),
          }
        : undefined,
    nextPeriod:
      nextSprint?.startDate && nextSprint?.endDate
        ? {
            start: new Date(nextSprint?.startDate || ''),
            end: new Date(nextSprint?.endDate || ''),
          }
        : undefined,
  };
};

/**
 * Creates DatePicker props including min/max dates and highlight periods
 */
export const createDatePickerProps = (
  selectedDate: Date,
  handleDateChange: (date: Date) => void,
  sprints: Sprint[] | undefined,
  selectedSprint: Sprint | null,
): DatePickerProps => {
  if (!sprints?.length) {
    return {
      value: selectedDate,
      onChange: handleDateChange,
    };
  }

  // Get min/max dates from earliest and latest sprints
  const minDate = new Date(sprints[sprints.length - 1]?.startDate || '');
  const maxDate = new Date(sprints[0]?.endDate || '');

  // Get highlight periods
  const highlightPeriods = {
    currentPeriod:
      selectedSprint?.startDate && selectedSprint?.endDate
        ? {
            start: new Date(selectedSprint?.startDate || ''),
            end: new Date(selectedSprint?.endDate || ''),
          }
        : undefined,
  };

  return {
    value: selectedDate,
    onChange: handleDateChange,
    minDate,
    maxDate,
    ...highlightPeriods,
  };
};
